import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ShopListItem } from '../interfaces/components';

interface TagWeight {
  tag: string;
  weight: number;
}

interface ShopStore {
  // Shops state
  shops: ShopListItem[];
  visitedThemes: string[];
  isLoading: boolean;
  setShops: (shops: ShopListItem[]) => void;
  setLoading: (loading: boolean) => void;

  // User Preferences state
  tagWeights: TagWeight[];
  visitedStores: string[]; // Stack-like structure with most recent at index 0
  lastStore: string | null;
  currentStore: ShopListItem | null;
  // currentStoreStartTime: number | null; // Temporarily disabled time tracking
  hasInteracted: boolean;
  addInteraction: (
    storeId: string,
    interactionType: 'product_click' | 'add_to_cart' | 'video_open' | 'other',
    tags?: string[],
  ) => void;
  getNextStore: () => ShopListItem;
  resetVisitedStores: () => void;
  setCurrentStore: (storeId: string) => void;
  getLastShop: (removedStoreId: string, storeId: string) => ShopListItem | null;
  getStoreById: (storeId: string) => ShopListItem | null;
  setVisitedThemes: (themes: string[]) => void;
}

const INTERACTION_WEIGHTS = {
  product_click: 0.4,
  add_to_cart: 1,
  video_open: 0.02,
  other: 0.01,
  // time_spent: 0.01, // Temporarily disabled time weight
};

export const useShopStore = create<ShopStore>()(
  persist(
    (set, get) => ({
      shops: [],
      visitedThemes: [],
      isLoading: true,
      tagWeights: [],
      visitedStores: [],
      lastStore: null,
      currentStore: null,
      hasInteracted: false,
      // currentStoreStartTime: null, // Temporarily disabled time tracking

      setShops: (shops: ShopListItem[]) =>
        set(() => ({
          shops: shops,
          isLoading: false,
        })),
      setLoading: (loading: boolean) => set(() => ({ isLoading: loading })),
      setCurrentStore: (storeId: string) => {
        set((state) => {
          const currentStore = state.shops.find((s) => s.storeId === storeId);
          const filteredStores = state.visitedStores.filter((s) => s !== storeId);
          const newVisitedStores =
            storeId.length > 0
              ? [storeId, ...filteredStores].filter((s) => s !== '')
              : filteredStores.filter((s) => s !== '');

          return {
            // currentStoreStartTime: Date.now(), // Temporarily disabled time tracking
            currentStore: currentStore,
            lastStore: storeId,
            hasInteracted: false,
            visitedStores: newVisitedStores,
          };
        });
      },

      addInteraction: (
        storeId: string,
        interactionType: 'product_click' | 'add_to_cart' | 'video_open' | 'other',
        tags?: string[],
      ) => {
        const { shops } = get();
        const store = shops.find((s) => s.storeId === storeId);
        if (!store) return;

        set((state) => {
          const newTagWeights = [...state.tagWeights];
          const weight = INTERACTION_WEIGHTS[interactionType];

          // For product interactions, use the provided tags
          if (interactionType === 'product_click' || interactionType === 'add_to_cart') {
            if (tags && tags.length > 0) {
              tags.forEach((tag: string) => {
                const existingTag = newTagWeights.find((t) => t.tag === tag);
                if (existingTag) {
                  existingTag.weight += weight;
                } else {
                  newTagWeights.push({ tag, weight });
                }
              });
            }
          }
          // For video and time interactions, use store theme tags
          else {
            store.tags.picked.forEach((tag: string) => {
              const existingTag = newTagWeights.find((t) => t.tag === tag);
              if (existingTag) {
                existingTag.weight += weight;
              } else {
                newTagWeights.push({ tag, weight });
              }
            });
          }

          return {
            tagWeights: newTagWeights,
            hasInteracted: true,
          };
        });
      },

      getNextStore: () => {
        const { tagWeights, visitedStores, /* currentStoreStartTime, */ shops, lastStore, currentStore } = get();

        // Check if all shops have been visited
        if (visitedStores.length === shops.length) {
          set(() => ({
            visitedStores: [], // Reset visited stores instead of removing oldest
          }));
        } else {
          set(() => ({
            visitedStores: visitedStores.filter((s) => s !== ''),
          }));
        }
        const currentVisitedStores = get().visitedStores;

        // Get the current store to avoid same theme and same store
        // const currentStore = shops.find((store: ShopListItem) => store.storeId === lastStore);
        const currentTheme = currentStore?.theme;

        // Maintain visited themes array
        let currentVisitedThemes = get().visitedThemes;

        // Add current theme to visited themes if not already there
        if (currentTheme && !currentVisitedThemes.includes(currentTheme)) {
          currentVisitedThemes.push(currentTheme);
        }

        // Reset visited themes if 7 different themes have been visited
        if (currentVisitedThemes.length >= 7) {
          // currentVisitedThemes = currentTheme ? [currentTheme] : [];
          currentVisitedThemes = [];
          set(() => ({
            visitedThemes: currentVisitedThemes,
          }));
        } else {
          set(() => ({
            visitedThemes: currentVisitedThemes,
          }));
        }

        // If no stores have been visited or no tag weights exist
        if (currentVisitedStores.length === 0 && tagWeights.length === 0) {
          // Find the most recent creation date from unvisited stores
          const mostRecentDate = Math.max(
            ...shops
              .filter((store: ShopListItem) => !currentVisitedStores.includes(store.storeId))
              .map((store: ShopListItem) => new Date(store.createdAt).getTime()),
          );

          // Get all stores with the most recent date
          const mostRecentStores = shops.filter((store: ShopListItem) => {
            const isRecentDate = new Date(store.createdAt).getTime() === mostRecentDate;
            const notVisited = !currentVisitedStores.includes(store.storeId);
            return isRecentDate && notVisited;
          });

          if (mostRecentStores.length === 1) {
            return mostRecentStores[0];
          }

          const randomIndex = Math.floor(Math.random() * mostRecentStores.length);

          // Add current theme to visited themes if not already there
          if (mostRecentStores[randomIndex] && !currentVisitedThemes.includes(mostRecentStores[randomIndex].theme)) {
            set(() => ({
              visitedThemes: [...currentVisitedThemes, mostRecentStores[randomIndex].theme],
            }));
          }

          return mostRecentStores[randomIndex];
        }

        // First try to get stores with themes not in visited themes and exclude current store
        let storeScores = shops
          .filter((store: ShopListItem) => {
            const notVisited = !currentVisitedStores.includes(store.storeId);
            const themeNotVisited = !currentVisitedThemes.includes(store.theme);
            // const notCurrentStore = store.storeId !== lastStore; // Exclude current store
            return notVisited && themeNotVisited;
          })
          .map((store: ShopListItem) => {
            const score = store.tags.picked.reduce((total: number, tag: string) => {
              const tagWeight = tagWeights.find((t) => t.tag.includes(tag) || tag.includes(t.tag));
              const baseWeight = tagWeight?.weight || 0;

              // If the tag exists in productTagCount, multiply the weight by the count
              const productCount = store.tags.productTagCount?.[tag] || 1;
              const finalWeight = baseWeight * productCount;

              return total + finalWeight;
            }, 0);

            // Add recency bonus to the score
            const creationDate = new Date(store.createdAt).getTime();
            const currentTime = Date.now();
            const ageInDays = (currentTime - creationDate) / (1000 * 60 * 60 * 24);
            const recencyBonus = Math.max(0, 30 - ageInDays); // Higher bonus for newer stores, increased from 10 to 30

            return { store, score: score + recencyBonus };
          });

        // If no stores with unvisited themes are available, fall back to any unvisited store with different theme than current (excluding current)
        if (storeScores.length === 0) {
          storeScores = shops
            .filter((store: ShopListItem) => {
              const notVisited = !currentVisitedStores.includes(store.storeId);
              const differentTheme = store.theme !== currentTheme;
              const notCurrentStore = store.storeId !== lastStore; // Exclude current store
              return notVisited && differentTheme && notCurrentStore;
            })
            .map((store: ShopListItem) => {
              const score = store.tags.picked.reduce((total: number, tag: string) => {
                const tagWeight = tagWeights.find((t) => t.tag.includes(tag) || tag.includes(t.tag));
                const baseWeight = tagWeight?.weight || 0;

                // If the tag exists in productTagCount, multiply the weight by the count
                const productCount = store.tags.productTagCount?.[tag] || 1;
                const finalWeight = baseWeight * productCount;

                return total + finalWeight;
              }, 0);

              // Add recency bonus to the score
              const creationDate = new Date(store.createdAt).getTime();
              const currentTime = Date.now();
              const ageInDays = (currentTime - creationDate) / (1000 * 60 * 60 * 24);
              const recencyBonus = Math.max(0, 30 - ageInDays);

              return { store, score: score + recencyBonus };
            });
        }

        // If there are no unvisited stores left, return the last store in the list
        if (storeScores.length === 0) {
          return shops[shops.length - 1];
        }

        // If there's only one unvisited store left, return it regardless of score
        if (storeScores.length === 1) {
          return storeScores[0].store;
        }

        const finalStore = storeScores.reduce(
          (prev: { score: number; store: ShopListItem }, current: { score: number; store: ShopListItem }) =>
            current.score > prev.score ? current : prev,
        ).store;

        // Add current theme to visited themes if not already there
        if (finalStore && !currentVisitedThemes.includes(finalStore.theme)) {
          set(() => ({
            visitedThemes: [...currentVisitedThemes, finalStore.theme],
          }));
        }

        return finalStore;
      },

      resetVisitedStores: () => {
        set({ visitedStores: [] });
      },

      getLastShop: (removedStoreId: string, storeId: string) => {
        const { shops } = get();
        const shop = shops.find((s: ShopListItem) => s.storeId === storeId);
        if (shop) {
          // Remove the current store from visitedStores if it exists
          set((state) => ({
            visitedStores: state.visitedStores.filter((id) => id !== removedStoreId),
          }));
          return shop;
        }
        return null;
      },

      getStoreById: (storeId: string) => {
        const { shops } = get();
        return shops.find((s: ShopListItem) => s.storeId === storeId) || null;
      },

      setVisitedThemes: (themes: string[]) => {
        set({ visitedThemes: themes });
      },
    }),
    {
      name: 'combined-store',
      partialize: (state) => ({
        tagWeights: state.tagWeights,
        visitedStores: state.visitedStores,
        visitedThemes: state.visitedThemes,
      }),
    },
  ),
);
